// client/src/utils/pokerStats/emptyStats.ts
//
// Factory para HistoryStats vacío + función para mergear (sumar) dos HistoryStats.
// Este archivo es el único que necesita conocer TODOS los campos de HistoryStats:
// agregar un campo nuevo = tocar solo este archivo.

import { HistoryStats, Position, MatchupKey, MatchupStats, OpenPosition, RfiPositionStats, PushFoldStats, CbetTexturePosKey, CbetTextureStats, CbetTextureMultiKey, VpipPositionStats, ShoverStackBin, FoldToPushStats } from './types';

export function emptyStats(): HistoryStats {
  return {
    handsPlayed: 0,
    positionCounts: { BTN: 0, SB: 0, BB: 0, CO: 0, HJ: 0, EP: 0, OTHER: 0 },

    vpipCount: 0,
    pfrCount: 0,
    threebetCount: 0,
    threebetOpp: 0,
    foldTo3betCount: 0,
    foldTo3betOpp: 0,
    foldTo3betAllinOpp: 0,
    foldTo3betAllinCount: 0,
    foldTo3betNonAllinOpp: 0,
    foldTo3betNonAllinCount: 0,

    // Fase 4: matchup breakdowns del 3bet — empiezan vacíos
    threebetByMatchup: {},
    foldTo3betByMatchup: {},

    // Fase C: Fold to Push — empieza en cero
    foldToPushOpp: 0,
    foldToPushCount: 0,
    foldToPushByShoverStack: {},

    // VPIP por posición — empieza vacío, se popula on-demand
    vpipByPosition: {},

    // Fase 3: RFI por posición — empieza vacío, se popula on-demand
    rfiByPosition: {},

    // Fase A: Push/Fold short stack — empieza vacío, se popula on-demand
    pushFoldByPosition: {},

    // Fase B: CBet Flop por textura — empieza vacío
    cbetFlopByTexture: {},

    // Fase B.2: CBet Flop por textura multiway — empieza vacío
    cbetFlopByTextureMulti: {},

    atsCount: 0,
    atsOpp: 0,

    afBets: 0,
    afRaises: 0,
    afCalls: 0,
    afChecks: 0,

    cbetFlopCount: 0,
    cbetFlopOpp: 0,
    cbetFlopSuccessCount: 0,
    cbetTurnCount: 0,
    cbetTurnOpp: 0,
    cbetTurnSuccessCount: 0,
    cbetRiverCount: 0,
    cbetRiverOpp: 0,
    cbetRiverSuccessCount: 0,

    cbetFlopMatchups: {},

    foldToCbetFlopCount: 0,
    foldToCbetFlopOpp: 0,
    callCbetFlopCount: 0,
    foldToCbetTurnCount: 0,
    foldToCbetTurnOpp: 0,
    callCbetTurnCount: 0,
    foldToCbetRiverCount: 0,
    foldToCbetRiverOpp: 0,
    callCbetRiverCount: 0,

    donkFlopCount: 0,
    donkFlopOpp: 0,
    donkTurnCount: 0,
    donkTurnOpp: 0,
    donkRiverCount: 0,
    donkRiverOpp: 0,

    probeTurnCount: 0,
    probeTurnOpp: 0,
    probeRiverCount: 0,
    probeRiverOpp: 0,

    flopsSeen: 0,
    wtsdCount: 0,
    wsdCount: 0,
    wsdOpp: 0,
    wwsfCount: 0,
    wwsfOpp: 0,

    bbDefendOpp: 0,
    bbDefendCall: 0,
    bbDefendRaise: 0,
    bbDefendFold: 0,
    flopDryCount: 0,
    cbetFlopDryCount: 0,
    cbetFlopDrySuccessCount: 0,
    btnStealOpp: 0,
    btnStealCount: 0,
    sbStealOpp: 0,
    sbStealCount: 0,
    doubleBarrelOpp: 0,
    doubleBarrelCount: 0,
    delayCbetOpp: 0,
    delayCbetCount: 0,
    cbet3BetPotOpp: 0,
    cbet3BetPotCount: 0,
    cbet3BetPotIPOpp: 0,
    cbet3BetPotIPCount: 0,
    cbet3BetPotOOPOpp: 0,
    cbet3BetPotOOPCount: 0,
    squeezeOpp: 0,
    squeezeCount: 0,
    fourbetOpp: 0,
    fourbetCount: 0,

    xraiseFlopOpp: 0,
    xraiseFlopCount: 0,
    xraiseFlopAsPFAOpp: 0,
    xraiseFlopAsPFACount: 0,
    xraiseFlopNonPFAOpp: 0,
    xraiseFlopNonPFACount: 0,

    bountiesCollected: 0,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// addStats — suma src en dest (in-place en dest). NO muta src.
// Usar para agregar manos al torneo o torneos al agregado global.
// ─────────────────────────────────────────────────────────────────────────────

// Helper: suma opcional con fallback 0
function addOpt(destVal: number | undefined, srcVal: number | undefined): number {
  return (destVal || 0) + (srcVal || 0);
}

// Helper: merge dos maps de MatchupStats. Suma todos los buckets de src en dest.
function mergeMatchupMaps(
  dest: Partial<Record<MatchupKey, MatchupStats>>,
  src: Partial<Record<MatchupKey, MatchupStats>>
): void {
  for (const key of Object.keys(src) as MatchupKey[]) {
    const srcBucket = src[key];
    if (!srcBucket) continue;
    const destBucket: MatchupStats = dest[key] || { opp: 0, count: 0, success: 0 };
    destBucket.opp += srcBucket.opp;
    destBucket.count += srcBucket.count;
    destBucket.success += srcBucket.success;
    dest[key] = destBucket;
  }
}

export function addStats(dest: HistoryStats, src: HistoryStats): void {
  dest.handsPlayed += src.handsPlayed;

  // Preflop core
  dest.vpipCount += src.vpipCount;
  dest.pfrCount += src.pfrCount;
  dest.threebetCount += src.threebetCount;
  dest.threebetOpp += src.threebetOpp;
  dest.foldTo3betCount += src.foldTo3betCount;
  dest.foldTo3betOpp += src.foldTo3betOpp;

  // Fase 2: splits
  dest.foldTo3betAllinOpp = addOpt(dest.foldTo3betAllinOpp, src.foldTo3betAllinOpp);
  dest.foldTo3betAllinCount = addOpt(dest.foldTo3betAllinCount, src.foldTo3betAllinCount);
  dest.foldTo3betNonAllinOpp = addOpt(dest.foldTo3betNonAllinOpp, src.foldTo3betNonAllinOpp);
  dest.foldTo3betNonAllinCount = addOpt(dest.foldTo3betNonAllinCount, src.foldTo3betNonAllinCount);

  // Fase C: Fold to Push — counters globales
  dest.foldToPushOpp = addOpt(dest.foldToPushOpp, src.foldToPushOpp);
  dest.foldToPushCount = addOpt(dest.foldToPushCount, src.foldToPushCount);

  // Fase C: Fold to Push — por stack bin del shover
  if (src.foldToPushByShoverStack) {
    if (!dest.foldToPushByShoverStack) dest.foldToPushByShoverStack = {};
    for (const bin of Object.keys(src.foldToPushByShoverStack) as ShoverStackBin[]) {
      const srcBucket = src.foldToPushByShoverStack[bin];
      if (!srcBucket) continue;
      const destBucket: FoldToPushStats = dest.foldToPushByShoverStack[bin] || { opp: 0, folds: 0 };
      destBucket.opp += srcBucket.opp;
      destBucket.folds += srcBucket.folds;
      dest.foldToPushByShoverStack[bin] = destBucket;
    }
  }

  // VPIP por posición
  if (src.vpipByPosition) {
    if (!dest.vpipByPosition) dest.vpipByPosition = {};
    for (const pos of Object.keys(src.vpipByPosition) as Position[]) {
      const srcBucket = src.vpipByPosition[pos];
      if (!srcBucket) continue;
      const destBucket: VpipPositionStats = dest.vpipByPosition[pos] || { opp: 0, count: 0 };
      destBucket.opp += srcBucket.opp;
      destBucket.count += srcBucket.count;
      dest.vpipByPosition[pos] = destBucket;
    }
  }

  // Fase 3: RFI por posición
  if (src.rfiByPosition) {
    if (!dest.rfiByPosition) dest.rfiByPosition = {};
    for (const pos of Object.keys(src.rfiByPosition) as OpenPosition[]) {
      const srcBucket = src.rfiByPosition[pos];
      if (!srcBucket) continue;
      const destBucket: RfiPositionStats = dest.rfiByPosition[pos] || { opp: 0, count: 0 };
      destBucket.opp += srcBucket.opp;
      destBucket.count += srcBucket.count;
      destBucket.limps = (destBucket.limps || 0) + (srcBucket.limps || 0);
      dest.rfiByPosition[pos] = destBucket;
    }
  }

  // Fase A: Push/Fold short stack por posición
  if (src.pushFoldByPosition) {
    if (!dest.pushFoldByPosition) dest.pushFoldByPosition = {};
    for (const pos of Object.keys(src.pushFoldByPosition) as OpenPosition[]) {
      const srcBucket = src.pushFoldByPosition[pos];
      if (!srcBucket) continue;
      const destBucket: PushFoldStats = dest.pushFoldByPosition[pos] || {
        opp: 0,
        pushes: 0,
        limps: 0,
        minRaises: 0,
        folds: 0,
      };
      destBucket.opp += srcBucket.opp;
      destBucket.pushes += srcBucket.pushes;
      destBucket.limps += srcBucket.limps;
      destBucket.minRaises += srcBucket.minRaises;
      destBucket.folds += srcBucket.folds;
      dest.pushFoldByPosition[pos] = destBucket;
    }
  }

  // Fase B: CBet Flop por textura × posición
  if (src.cbetFlopByTexture) {
    if (!dest.cbetFlopByTexture) dest.cbetFlopByTexture = {};
    for (const key of Object.keys(src.cbetFlopByTexture) as CbetTexturePosKey[]) {
      const srcBucket = src.cbetFlopByTexture[key];
      if (!srcBucket) continue;
      const destBucket: CbetTextureStats = dest.cbetFlopByTexture[key] || { opp: 0, count: 0 };
      destBucket.opp += srcBucket.opp;
      destBucket.count += srcBucket.count;
      dest.cbetFlopByTexture[key] = destBucket;
    }
  }

  // Fase B.2: CBet Flop por textura — multiway
  if (src.cbetFlopByTextureMulti) {
    if (!dest.cbetFlopByTextureMulti) dest.cbetFlopByTextureMulti = {};
    for (const key of Object.keys(src.cbetFlopByTextureMulti) as CbetTextureMultiKey[]) {
      const srcBucket = src.cbetFlopByTextureMulti[key];
      if (!srcBucket) continue;
      const destBucket: CbetTextureStats = dest.cbetFlopByTextureMulti[key] || { opp: 0, count: 0 };
      destBucket.opp += srcBucket.opp;
      destBucket.count += srcBucket.count;
      dest.cbetFlopByTextureMulti[key] = destBucket;
    }
  }

  dest.atsCount += src.atsCount;
  dest.atsOpp += src.atsOpp;

  // Aggression
  dest.afBets += src.afBets;
  dest.afRaises += src.afRaises;
  dest.afCalls += src.afCalls;
  dest.afChecks += src.afChecks;

  // CBet
  dest.cbetFlopCount += src.cbetFlopCount;
  dest.cbetFlopOpp += src.cbetFlopOpp;
  dest.cbetFlopSuccessCount += src.cbetFlopSuccessCount;
  dest.cbetTurnCount += src.cbetTurnCount;
  dest.cbetTurnOpp += src.cbetTurnOpp;
  dest.cbetTurnSuccessCount += src.cbetTurnSuccessCount;
  dest.cbetRiverCount += src.cbetRiverCount;
  dest.cbetRiverOpp += src.cbetRiverOpp;
  dest.cbetRiverSuccessCount += src.cbetRiverSuccessCount;

  // Fase 1: matchup breakdown del cbet flop
  if (src.cbetFlopMatchups) {
    if (!dest.cbetFlopMatchups) dest.cbetFlopMatchups = {};
    mergeMatchupMaps(dest.cbetFlopMatchups, src.cbetFlopMatchups);
  }

  // Fase 4: matchup breakdowns del 3bet
  if (src.threebetByMatchup) {
    if (!dest.threebetByMatchup) dest.threebetByMatchup = {};
    mergeMatchupMaps(dest.threebetByMatchup, src.threebetByMatchup);
  }
  if (src.foldTo3betByMatchup) {
    if (!dest.foldTo3betByMatchup) dest.foldTo3betByMatchup = {};
    mergeMatchupMaps(dest.foldTo3betByMatchup, src.foldTo3betByMatchup);
  }

  // Defense
  dest.foldToCbetFlopCount += src.foldToCbetFlopCount;
  dest.foldToCbetFlopOpp += src.foldToCbetFlopOpp;
  dest.callCbetFlopCount += src.callCbetFlopCount;
  dest.foldToCbetTurnCount += src.foldToCbetTurnCount;
  dest.foldToCbetTurnOpp += src.foldToCbetTurnOpp;
  dest.callCbetTurnCount += src.callCbetTurnCount;
  dest.foldToCbetRiverCount += src.foldToCbetRiverCount;
  dest.foldToCbetRiverOpp += src.foldToCbetRiverOpp;
  dest.callCbetRiverCount += src.callCbetRiverCount;

  // Donk / probe
  dest.donkFlopCount += src.donkFlopCount;
  dest.donkFlopOpp += src.donkFlopOpp;
  dest.donkTurnCount += src.donkTurnCount;
  dest.donkTurnOpp += src.donkTurnOpp;
  dest.donkRiverCount += src.donkRiverCount;
  dest.donkRiverOpp += src.donkRiverOpp;
  dest.probeTurnCount += src.probeTurnCount;
  dest.probeTurnOpp += src.probeTurnOpp;
  dest.probeRiverCount += src.probeRiverCount;
  dest.probeRiverOpp += src.probeRiverOpp;

  // Showdown
  dest.flopsSeen += src.flopsSeen;
  dest.wtsdCount += src.wtsdCount;
  dest.wsdCount += src.wsdCount;
  dest.wsdOpp += src.wsdOpp;
  dest.wwsfCount += src.wwsfCount;
  dest.wwsfOpp += src.wwsfOpp;

  // Position breakdown
  for (const pos of Object.keys(src.positionCounts) as Position[]) {
    dest.positionCounts[pos] = (dest.positionCounts[pos] || 0) + (src.positionCounts[pos] || 0);
  }

  // Optional extras
  dest.bbDefendOpp = addOpt(dest.bbDefendOpp, src.bbDefendOpp);
  dest.bbDefendCall = addOpt(dest.bbDefendCall, src.bbDefendCall);
  dest.bbDefendRaise = addOpt(dest.bbDefendRaise, src.bbDefendRaise);
  dest.bbDefendFold = addOpt(dest.bbDefendFold, src.bbDefendFold);

  dest.flopDryCount = addOpt(dest.flopDryCount, src.flopDryCount);
  dest.cbetFlopDryCount = addOpt(dest.cbetFlopDryCount, src.cbetFlopDryCount);
  dest.cbetFlopDrySuccessCount = addOpt(dest.cbetFlopDrySuccessCount, src.cbetFlopDrySuccessCount);

  dest.btnStealOpp = addOpt(dest.btnStealOpp, src.btnStealOpp);
  dest.btnStealCount = addOpt(dest.btnStealCount, src.btnStealCount);
  dest.sbStealOpp = addOpt(dest.sbStealOpp, src.sbStealOpp);
  dest.sbStealCount = addOpt(dest.sbStealCount, src.sbStealCount);

  dest.doubleBarrelOpp = addOpt(dest.doubleBarrelOpp, src.doubleBarrelOpp);
  dest.doubleBarrelCount = addOpt(dest.doubleBarrelCount, src.doubleBarrelCount);
  dest.delayCbetOpp = addOpt(dest.delayCbetOpp, src.delayCbetOpp);
  dest.delayCbetCount = addOpt(dest.delayCbetCount, src.delayCbetCount);
  dest.cbet3BetPotOpp = addOpt(dest.cbet3BetPotOpp, src.cbet3BetPotOpp);
  dest.cbet3BetPotCount = addOpt(dest.cbet3BetPotCount, src.cbet3BetPotCount);
  dest.cbet3BetPotIPOpp = addOpt(dest.cbet3BetPotIPOpp, src.cbet3BetPotIPOpp);
  dest.cbet3BetPotIPCount = addOpt(dest.cbet3BetPotIPCount, src.cbet3BetPotIPCount);
  dest.cbet3BetPotOOPOpp = addOpt(dest.cbet3BetPotOOPOpp, src.cbet3BetPotOOPOpp);
  dest.cbet3BetPotOOPCount = addOpt(dest.cbet3BetPotOOPCount, src.cbet3BetPotOOPCount);
  dest.bountiesCollected = addOpt(dest.bountiesCollected, src.bountiesCollected);

  dest.squeezeOpp   = addOpt(dest.squeezeOpp,   src.squeezeOpp);
  dest.squeezeCount = addOpt(dest.squeezeCount, src.squeezeCount);
  dest.fourbetOpp   = addOpt(dest.fourbetOpp,   src.fourbetOpp);
  dest.fourbetCount = addOpt(dest.fourbetCount, src.fourbetCount);

  dest.xraiseFlopOpp = addOpt(dest.xraiseFlopOpp, src.xraiseFlopOpp);
  dest.xraiseFlopCount = addOpt(dest.xraiseFlopCount, src.xraiseFlopCount);
  dest.xraiseFlopAsPFAOpp = addOpt(dest.xraiseFlopAsPFAOpp, src.xraiseFlopAsPFAOpp);
  dest.xraiseFlopAsPFACount = addOpt(dest.xraiseFlopAsPFACount, src.xraiseFlopAsPFACount);
  dest.xraiseFlopNonPFAOpp = addOpt(dest.xraiseFlopNonPFAOpp, src.xraiseFlopNonPFAOpp);
  dest.xraiseFlopNonPFACount = addOpt(dest.xraiseFlopNonPFACount, src.xraiseFlopNonPFACount);
}
